/**
 * The cards entity's merge: whole-row LAST-WRITER-WINS on the composite
 * clock (`updatedAtISO`) for every field — exactly as before — EXCEPT the
 * review history, which only grows unless a removal is EXPLICIT.
 *
 * Why: with whole-row LWW, a row carrying less history than the stored one
 * replaced it whenever its clock was newer. A device that rebuilt a group
 * deck from a short disk read, a member studying a twin copy, a phone whose
 * offline grade lost to a desktop row stamped later: the first write on such
 * a row erased the history everywhere.
 *
 * The rule, on two rows of one card:
 *  - the WINNER is the row with the newer `updatedAtISO` (ties go to
 *    `incoming`, as the server's default LWW does); every field except the
 *    history is the winner's;
 *  - `studyState` and the `good`/`bad` actions are the UNION of both rows'
 *    entries (identity: kind + ISO), sorted by ISO;
 *  - `undoneISO` is the union of both rows' tombstones, and every history
 *    entry whose ISO it names is removed — that is the ONLY way a review
 *    leaves a row through this merge;
 *  - `delete` actions follow the winner: `isCardDeleted` reads them and
 *    "undo delete" filters them out, so a union would keep an undone delete.
 *
 * Nothing is inferred from a row's shape or from any client clock. A shorter
 * row is never read as an Undo: the device says so with `undoneISO`, or —
 * for installed builds, which cannot — the SERVER says so after checking
 * that the same connection delivered the review moments ago
 * (`party/server/user/card-history-intent.ts`). A progress reset is not this
 * merge's job either: the deck floor cleans rows on both sides
 * (`party/server/user/progress-floor.ts`, `internal/store/progress-floor.ts`).
 *
 * It is a join over the history (idempotent, commutative, associative: every
 * entry carries its own identity, and tombstones only grow), which is what
 * lets the client fold broadcasts with it (`foldBroadcasts`).
 *
 * When the merge changes nothing it returns an input BY REFERENCE, so the
 * server can treat an idempotent re-push as a no-op (no write, no broadcast).
 *
 * {@link setCardHistoryJoinDisabled} turns it into plain LWW on the process
 * that calls it: the client, until the server's `sync-features` frame says it
 * unions history.
 */

type ActionEntry = Partial<Record<'good' | 'bad' | 'delete', { ISO?: string }>>;
type StudyEntry = { type?: string; ISO?: string };

/** The fields this merge reads; every other field is carried as-is. */
export type CardHistoryRow = {
  updatedAtISO?: string;
  studyState?: unknown[];
  actions?: unknown[];
  undoneISO?: string[];
};

let disabled = false;

/** Kill switch: `true` makes {@link joinCardHistory} plain whole-row LWW. */
export function setCardHistoryJoinDisabled(value: boolean): void {
  disabled = value;
}

export function isCardHistoryJoinDisabled(): boolean {
  return disabled;
}

function actionKind(entry: unknown): 'good' | 'bad' | 'delete' | undefined {
  const a = entry as ActionEntry | null;
  if (!a || typeof a !== 'object') return undefined;
  if (a.delete) return 'delete';
  if (a.good) return 'good';
  if (a.bad) return 'bad';
  return undefined;
}

/** The ISO a history entry is identified by ('' when it has none). */
export function historyEntryISO(entry: unknown): string {
  const kind = actionKind(entry);
  if (kind) return (entry as ActionEntry)[kind]?.ISO ?? '';
  const iso = (entry as StudyEntry | null)?.ISO;
  return typeof iso === 'string' ? iso : '';
}

export function studyKey(entry: unknown): string {
  const e = entry as StudyEntry | null;
  return e && typeof e.ISO === 'string'
    ? `${e.type ?? ''}|${e.ISO}`
    : JSON.stringify(entry);
}

export function actionKey(entry: unknown): string {
  const kind = actionKind(entry);
  const iso = kind ? (entry as ActionEntry)[kind]?.ISO : undefined;
  return kind && typeof iso === 'string' ? `${kind}|${iso}` : JSON.stringify(entry);
}

/** A good/bad action — part of the review history. */
export const isReviewAction = (entry: unknown): boolean => {
  const kind = actionKind(entry);
  return kind === 'good' || kind === 'bad';
};

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/** `kept` plus the entries of `other` it lacks (filtered), ISO-sorted; the
 *  same array when nothing is added. */
function unionLog(
  kept: unknown[] | undefined,
  other: unknown[] | undefined,
  key: (e: unknown) => string,
  accept: (e: unknown) => boolean = () => true,
): unknown[] | undefined {
  if (!other || other.length === 0) return kept;
  const have = new Set(asArray(kept).map(key));
  const missing = other.filter((e) => accept(e) && !have.has(key(e)));
  if (missing.length === 0) return kept;
  const all = [...asArray(kept), ...missing];
  // Stable sort by ISO: entries the winner already ordered keep their order.
  return all
    .map((e, i) => ({ e, i, iso: historyEntryISO(e) }))
    .sort((a, b) => (a.iso < b.iso ? -1 : a.iso > b.iso ? 1 : a.i - b.i))
    .map((x) => x.e);
}

/** `list` without the history entries `undone` names; the same array when
 *  nothing is removed. `keep` exempts entries that are not history. */
function withoutUndone(
  list: unknown[] | undefined,
  undone: ReadonlySet<string>,
  keep: (e: unknown) => boolean = () => false,
): unknown[] | undefined {
  if (!list || undone.size === 0) return list;
  const out = list.filter((e) => keep(e) || !undone.has(historyEntryISO(e)));
  return out.length === list.length ? list : out;
}

/** Both rows' tombstones; the winner's own array when the loser adds none. */
function unionUndone(
  winner: string[] | undefined,
  loser: string[] | undefined,
): string[] | undefined {
  if (!loser || loser.length === 0) return winner;
  const have = new Set(winner ?? []);
  const missing = loser.filter((iso) => typeof iso === 'string' && !have.has(iso));
  if (missing.length === 0) return winner;
  return [...(winner ?? []), ...missing].sort();
}

/**
 * Merge two rows of one card (see the module comment). `base` is the row the
 * caller holds (the server's stored row, or the server row a client folds
 * its local one into); `incoming` the other.
 */
export function joinCardHistory<T extends CardHistoryRow>(base: T, incoming: T): T {
  const incomingWins = (incoming.updatedAtISO ?? '') >= (base.updatedAtISO ?? '');
  const winner = incomingWins ? incoming : base;
  if (disabled) return winner;
  const loser = incomingWins ? base : incoming;

  const undoneISO = unionUndone(winner.undoneISO, loser.undoneISO);
  const undone = new Set(undoneISO ?? []);
  const studyState = withoutUndone(
    unionLog(winner.studyState, loser.studyState, studyKey),
    undone,
  );
  const actions = withoutUndone(
    unionLog(winner.actions, loser.actions, actionKey, isReviewAction),
    undone,
    (e) => !isReviewAction(e),
  );
  if (
    studyState === winner.studyState &&
    actions === winner.actions &&
    undoneISO === winner.undoneISO
  ) {
    return winner;
  }
  return {
    ...winner,
    ...(studyState !== winner.studyState ? { studyState } : {}),
    ...(actions !== winner.actions ? { actions } : {}),
    ...(undoneISO !== winner.undoneISO ? { undoneISO } : {}),
  };
}

/** Every history ISO a row holds (study entries and good/bad actions). */
export function historyISOs(row: CardHistoryRow): Set<string> {
  const out = new Set<string>();
  for (const e of asArray(row.studyState)) {
    const iso = historyEntryISO(e);
    if (iso) out.add(iso);
  }
  for (const e of asArray(row.actions)) {
    if (!isReviewAction(e)) continue;
    const iso = historyEntryISO(e);
    if (iso) out.add(iso);
  }
  return out;
}

/**
 * An Undo, written explicitly: `card` without the history entries made at
 * `isos`, and those ISOs added to its `undoneISO` tombstones so no other
 * device's copy brings them back. Every field other than the history and the
 * tombstones is `card`'s own — the caller stamps the write as usual.
 */
export function undoCardReviews<T extends CardHistoryRow>(
  card: T,
  isos: readonly string[],
): T {
  const named = isos.filter((iso) => typeof iso === 'string' && iso !== '');
  if (named.length === 0) return card;
  const undone = new Set(named);
  const studyState = withoutUndone(card.studyState, undone) ?? [];
  const actions = withoutUndone(card.actions, undone, (e) => !isReviewAction(e)) ?? [];
  const undoneISO = unionUndone(card.undoneISO, named) ?? named;
  return { ...card, studyState, actions, undoneISO };
}

/**
 * `row` with the reviews and tombstones of `from` it lacks — every other
 * field stays `row`'s, and `keep` filters which of `from`'s reviews may come
 * across (a deck's progress floor). The same reference when nothing is
 * added. Used to fold a twin copy of a group deck into the copy that stays
 * (`party/server/user/sg-twin-merge.ts`).
 */
export function unionCardHistory<T extends CardHistoryRow>(
  row: T,
  from: CardHistoryRow,
  keep: (entry: unknown) => boolean = () => true,
): T {
  const undoneISO = unionUndone(row.undoneISO, from.undoneISO);
  const undone = new Set(undoneISO ?? []);
  const studyState = withoutUndone(
    unionLog(row.studyState, from.studyState, studyKey, keep),
    undone,
  );
  const actions = withoutUndone(
    unionLog(row.actions, from.actions, actionKey, (e) => isReviewAction(e) && keep(e)),
    undone,
    (e) => !isReviewAction(e),
  );
  if (
    studyState === row.studyState &&
    actions === row.actions &&
    undoneISO === row.undoneISO
  ) {
    return row;
  }
  return { ...row, studyState, actions, undoneISO };
}
